import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import Container from '../ui/Container';
import { getProductsByGroup } from '../../data/products';
import './HeroV2.css';

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.09, delayChildren: 0.12 },
  },
};

const item = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] },
  },
};

const HeroV2 = () => {
  const reduced = useReducedMotion();
  const liveProducts = getProductsByGroup('live')
    .filter((product) => product.screenshots?.length)
    .slice(0, 2);

  return (
    <section className="ft-hero" aria-labelledby="ft-hero-title">
      <div className="ft-hero__atmosphere" aria-hidden="true" />
      <Container className="ft-hero__container">
        <div className="ft-hero__grid">
          <motion.div
            className="ft-hero__copy"
            variants={reduced ? undefined : stagger}
            initial={reduced ? false : 'hidden'}
            animate={reduced ? false : 'show'}
          >
            <motion.div className="ft-hero__eyebrow" variants={item}>
              <span className="ft-hero__eyebrow-dot" aria-hidden="true" />
              AI Product Studio
              <span className="ft-hero__eyebrow-location">Seoul</span>
            </motion.div>

            <motion.h1 id="ft-hero-title" className="ft-hero__title" variants={item}>
              작게 만들고,
              <br />
              빠르게 <span className="ft-hero__title-accent">검증합니다.</span>
            </motion.h1>

            <motion.p className="ft-hero__sub" variants={item}>
              Flowto.AI는 일상에서 실제로 쓰이는 AI 서비스를 만듭니다.
              <br className="ft-hero__sub-break" />
              작은 아이디어로 시작해, 빠르게 만들고 반응을 확인합니다.
            </motion.p>

            <motion.div className="ft-hero__cta" variants={item}>
              <Link to="/products" className="ft-hero__btn ft-hero__btn--primary">
                프로덕트 보기
                <ArrowRight size={18} strokeWidth={2.4} aria-hidden="true" />
              </Link>
              <Link to="/about" className="ft-hero__btn ft-hero__btn--text">
                회사 소개
                <ArrowUpRight size={17} strokeWidth={2.2} aria-hidden="true" />
              </Link>
            </motion.div>

            <motion.div className="ft-hero__process" variants={item} aria-label="Flowto.AI의 제품 개발 과정">
              {['아이디어', '프로토타입', '사용자 검증', '출시'].map((step, index) => (
                <React.Fragment key={step}>
                  <span className={index === 3 ? 'is-live' : ''}>{step}</span>
                  {index < 3 && <i aria-hidden="true" />}
                </React.Fragment>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="ft-hero__studio"
            aria-label="현재 서비스 중인 Flowto.AI 프로덕트"
            initial={reduced ? false : { opacity: 0, x: 34 }}
            animate={reduced ? false : { opacity: 1, x: 0 }}
            transition={{ duration: 0.85, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="ft-hero__studio-head">
              <div>
                <span className="ft-hero__studio-kicker">IN THE STUDIO</span>
                <strong>Ideas in motion</strong>
              </div>
              <span className="ft-hero__live"><i aria-hidden="true" /> BUILDING</span>
            </div>

            <div className="ft-hero__stage">
              {liveProducts.map((product, index) => (
                <Link
                  to={`/products/${product.slug}`}
                  key={product.slug}
                  className={`ft-hero__product ft-hero__product--${index + 1}`}
                  style={{ '--hero-product-color': product.color }}
                  aria-label={`${product.nameKo} 제품 자세히 보기`}
                >
                  <div className="ft-hero__preview">
                    <img
                      src={product.screenshots[0]}
                      alt={`${product.nameKo} 앱 화면`}
                      width={product.screenshotSizes?.[0]?.width ?? 540}
                      height={product.screenshotSizes?.[0]?.height ?? 960}
                      loading="eager"
                    />
                  </div>
                  <div className="ft-hero__product-label">
                    <img src={product.appIcon} alt="" />
                    <span>
                      <small>{product.theme === 'travel' ? 'TRAVEL' : 'HEALTH · HABIT'}</small>
                      <strong>{product.nameKo}</strong>
                    </span>
                    <ArrowUpRight size={16} strokeWidth={2} aria-hidden="true" />
                  </div>
                </Link>
              ))}
            </div>

            <div className="ft-hero__studio-foot" aria-hidden="true">
              <span>FLOWTO / PRODUCT LAB</span>
              <span>BUILD · TEST · LEARN</span>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default HeroV2;
